import React, { useState } from 'react';
import { Save, Edit2, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAdminPlatformAddresses } from '../../hooks/useAdminRealtime';

const DepositAddressManager = ({ userId, userEmail, onClose }) => {
  const { addresses: platformAddresses } = useAdminPlatformAddresses();
  const [addresses, setAddresses] = useState({});
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(!userId); // Platform edit mode by default if no userId

  // Initialize addresses
  React.useEffect(() => {
    if (userId) {
      fetchUserAddresses();
    } else {
      setAddresses(platformAddresses);
    }
  }, [userId, platformAddresses]);

  const fetchUserAddresses = async () => {
    try {
      const response = await api.get(`/deposit-addresses/user/${userId}`);
      setAddresses(response.data.addresses || {});
    } catch (error) {
      console.error('Error fetching user addresses:', error);
      toast.error('Failed to fetch deposit addresses');
    }
  };

  const cryptoNetworks = [
    { crypto: 'BTC', networks: ['Bitcoin'] },
    { crypto: 'ETH', networks: ['ERC20'] },
    { crypto: 'USDT', networks: ['ERC20', 'TRC20', 'BEP20'] },
    { crypto: 'USDC', networks: ['ERC20', 'TRC20', 'BEP20'] },
    { crypto: 'BNB', networks: ['BEP20', 'BEP2'] },
    { crypto: 'SOL', networks: ['Solana'] },
    { crypto: 'ADA', networks: ['Cardano'] },
    { crypto: 'XRP', networks: ['XRP'] },
    { crypto: 'DOT', networks: ['Polkadot'] }
  ];

  const handleAddressChange = (crypto, network, value) => {
    setAddresses(prev => ({
      ...prev,
      [crypto]: {
        ...(prev[crypto] || {}),
        [network]: value
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      if (userId) {
        // Update user-specific addresses
        await api.put(`/admin/deposit-addresses/user/${userId}`, {
          addresses: addresses
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('User deposit addresses updated successfully');
      } else {
        // Update platform addresses
        await api.put('/admin/deposit-addresses/platform', {
          addresses: addresses
        }, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        toast.success('Platform deposit addresses updated successfully');
      }
      
      setEditMode(false);
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving addresses:', error);
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to update addresses')
        : 'Failed to update addresses';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!userId) return;
    
    if (!window.confirm('Reset user addresses to platform defaults?')) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      await api.delete(`/admin/deposit-addresses/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('User addresses reset to platform defaults');
      fetchUserAddresses();
      if (onClose) onClose();
    } catch (error) {
      console.error('Error resetting addresses:', error);
      toast.error('Failed to reset addresses');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">
            {userId ? `Deposit Addresses for ${userEmail}` : 'Platform Deposit Addresses'}
          </h3>
          <p className="text-gray-400 text-sm">
            {userId ? 'Custom addresses for this user' : 'Default addresses for all users'}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm font-semibold flex items-center space-x-1"
            >
              <Edit2 className="h-4 w-4" />
              <span>Edit</span>
            </button>
          )}
          {userId && (
            <button
              onClick={handleResetToDefault}
              disabled={loading}
              className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm font-semibold flex items-center space-x-1 disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Reset to Default</span>
            </button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      {editMode && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3 flex items-start space-x-2">
          <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-400 font-semibold">Important</p>
            <p className="text-xs text-gray-300">
              {userId 
                ? 'These custom addresses will override platform defaults for this user. Users will see these addresses in their deposit section instantly.'
                : 'These addresses will be shown to all users who don\'t have custom addresses. Changes sync in real-time.'}
            </p>
          </div>
        </div>
      )}

      {/* Address Fields */}
      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {cryptoNetworks.map(({ crypto, networks }) => (
          <div key={crypto} className="bg-gray-800 rounded-lg p-4">
            <h4 className="text-white font-bold mb-3">{crypto}</h4>
            <div className="space-y-3">
              {networks.map(network => (
                <div key={network}>
                  <label className="text-gray-400 text-sm mb-1 block">{network}</label>
                  <input
                    type="text"
                    value={addresses[crypto]?.[network] || ''}
                    onChange={(e) => handleAddressChange(crypto, network, e.target.value)}
                    disabled={!editMode}
                    placeholder={`Enter ${crypto} address for ${network}`}
                    className="w-full px-3 py-2 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      {editMode && (
        <div className="flex items-center space-x-3">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2 disabled:opacity-50"
          >
            <Save className="h-5 w-5" />
            <span>{loading ? 'Saving...' : 'Save Changes'}</span>
          </button>
          <button
            onClick={() => setEditMode(false)}
            className="px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg text-white font-semibold"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default DepositAddressManager;
